import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LOGO_TRANSPARENT } from "@/lib/logoData";
import { useAuth } from "@/contexts/AuthContext";
import { Users, Video, AlertTriangle, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO = LOGO_TRANSPARENT; // v5.7.5 · logo HD

export default function DocenteDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/dashboard/teacher", { params: { teacher_id: user.id } }).then((r) => setData(r.data));
  }, [user]);

  if (!data) return <p className="text-[#6E7591] font-medium">Cargando…</p>;

  return (
    <div className="space-y-6" data-testid="docente-dashboard">
      {/* Cabecera ejecutiva corporativa */}
      <div className="rounded-lg bg-[#1F2547] text-white overflow-hidden">
        <div className="px-6 py-5 sm:px-8 sm:py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-16 w-28 sm:w-36 rounded-lg bg-white flex items-center justify-center shrink-0 px-3 py-2 shadow-sm">
              <img src={LOGO} alt="NEUROMATEMÁTICAS JASQ®" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFCC28]">Panel del docente</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight truncate">Tu jornada docente</h1>
              <p className="text-xs text-white/70 mt-0.5">NEUROMATEMÁTICAS JASQ® · Acompañamiento integral</p>
            </div>
          </div>
          <Link to="/docente/clases-en-vivo" data-testid="cta-go-classes"
            className="inline-flex items-center gap-2 bg-white text-[#1F2547] px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#FFCC28] transition-colors">
            Ver mis clases <ArrowRight size={15} />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="t-kpi-students" label="Estudiantes" value={data.students_count} hint="bajo tu acompañamiento" icon={Users} color="#315197" />
        <Kpi testid="t-kpi-upcoming" label="Próximas clases" value={data.upcoming_classes.length} hint="agendadas" icon={Video} color="#1F2547" />
        <Kpi testid="t-kpi-alerts" label="Alertas activas" value={data.alerts.length} hint="requieren atención" icon={AlertTriangle} color="#D63384" />
        <Kpi testid="t-kpi-rating" label="Rating" value={user?.rating || "—"} hint="valoración promedio" icon={Star} color="#FFCC28" iconColor="#9C7A18" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E1E5F0] rounded-lg p-6 lg:col-span-2" data-testid="upcoming-classes-list">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6E7591]">Agenda</p>
              <h3 className="font-display text-lg font-bold tracking-tight text-[#1F2547]">Próximas clases</h3>
            </div>
            <Link to="/docente/clases-en-vivo" className="text-xs font-semibold text-[#A43790] hover:underline">Ver todas</Link>
          </div>
          {data.upcoming_classes.length === 0 ? (
            <p className="text-sm text-[#6E7591]">No tienes clases agendadas.</p>
          ) : (
            <ul className="divide-y divide-[#E1E5F0]">
              {data.upcoming_classes.slice(0, 5).map((c) => {
                const student = data.students.find((s) => s.id === c.student_id);
                const dt = new Date(c.scheduled_at);
                return (
                  <li key={c.id} className="flex items-center justify-between py-3 px-2 -mx-2 rounded-md hover:bg-[#F7F8FA] transition-colors" data-testid={`upcoming-${c.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={student?.avatar} alt="" className="w-9 h-9 rounded-md bg-[#F7F8FA] border border-[#E1E5F0]" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#1F2547] truncate">{c.title}</p>
                        <p className="text-xs text-[#6E7591]">
                          {student?.name} · {dt.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                      </div>
                    </div>
                    <Link to={`/docente/clases-en-vivo/${c.id}/sala`}
                      className="text-xs font-semibold bg-[#1F2547] text-white px-3 py-1.5 rounded-md hover:bg-[#161A33] transition-colors">
                      Entrar
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#E1E5F0] rounded-lg p-6" data-testid="alerts-list">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6E7591]">Seguimiento</p>
          <h3 className="font-display text-lg font-bold tracking-tight text-[#1F2547] mb-4">Alertas pedagógicas</h3>
          {data.alerts.length === 0 ? (
            <p className="text-sm text-[#6E7591]">Sin alertas activas.</p>
          ) : (
            <ul className="space-y-3">
              {data.alerts.map((a, i) => (
                <li key={i} className="p-3 rounded-md border border-[#E1E5F0]">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                      a.type === "emotional" ? "bg-[#D63384]/12 text-[#A52258]" : "bg-[#FFCC28]/20 text-[#9C7A18]"
                    }`}>
                      {a.type === "emotional" ? "Emocional" : "Académica"}
                    </span>
                    <span className="text-sm font-semibold text-[#1F2547]">{a.student}</span>
                  </div>
                  <p className="text-xs text-[#6E7591] mt-1.5">{a.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ testid, label, value, hint, icon: Icon, color, iconColor }) {
  return (
    <div className="bg-white border border-[#E1E5F0] rounded-lg p-4 border-t-2 transition-all hover:shadow-sm hover:-translate-y-px"
      style={{ borderTopColor: color }} data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: `${color}14`, color: iconColor || color }}>
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] tracking-wider uppercase font-semibold text-[#6E7591] truncate">{label}</p>
          <p className="font-display text-2xl font-bold tracking-tight text-[#1F2547] truncate">{value}</p>
        </div>
      </div>
      {hint && <p className="text-[11px] text-[#6E7591] mt-2">{hint}</p>}
    </div>
  );
}
